<template>
  <div id="app" :class="{ 'app--with-tabbar': showTabbar }">
    <template v-if="isConfigLoaded">
      <router-view v-slot="{ Component }">
        <component :is="Component" />
      </router-view>

      <Teleport to="body">
        <van-tabbar
          v-if="showTabbar"
          route
          fixed
          class="custom-tabbar"
        >
          <van-tabbar-item
            v-for="(item, index) in menuList"
            :key="index"
            :to="item.path"
            :icon="item.icon"
            >{{ item.name }}</van-tabbar-item
          >
        </van-tabbar>
      </Teleport>
    </template>
    <div v-else class="loading-container">
      <van-loading type="spinner" color="#1a5fb4" size="24px" vertical>
        加载中...
      </van-loading>
    </div>
  </div>
</template>

<script setup>
import { computed, provide, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { getJiandu } from "@/api/user";
import { mallApi } from "@/api/mall";
import { getToken } from "@/utils/auth";
import { isActivityEnabled } from "@/utils/activity";

const BANK_DISBURSEMENT_NAMES = new Set([
  "银行下款300万元",
  "银行下款500万元",
  "银行下款1000万元",
  "银行下款1500万元",
  "银行下款火速到账1500万元",
]);
const PAID_STATUSES = new Set([1, 2, 3]);

const store = useStore();
const route = useRoute();
const overlayOpenCount = ref(0);
const jianduPaid = ref(false);
const hasBankDisbursementPaid = ref(false);

const setAppOverlayOpen = (open) => {
  if (open) {
    overlayOpenCount.value += 1;
    return;
  }
  overlayOpenCount.value = Math.max(0, overlayOpenCount.value - 1);
};

provide("setAppOverlayOpen", setAppOverlayOpen);

const isConfigLoaded = computed(() => {
  return !!store.state.config.webViewConfig;
});

const rawMenuList = computed(() => {
  const config = store.state.config.webViewConfig;
  return config?.page_menu_list || [];
});

const isWhiteMenu = (item) => {
  const path = String(item?.path || "");
  const name = String(item?.name || "");
  return path === "/white" || path.endsWith("/white") || name.includes("白名单");
};

const isHouseMenu = (item) => {
  const path = String(item?.path || "");
  const name = String(item?.name || "");
  return path === "/house" || path.endsWith("/house") || name.includes("安置房");
};

const productNameOf = (order) =>
  order?.product?.name || order?.product_detail?.name || "";

const refreshJianduPaid = async () => {
  if (!getToken()) {
    jianduPaid.value = false;
    return;
  }
  const profilePaid = !!store.state.user.userInfo?.is_jiandu;
  try {
    const res = await getJiandu();
    jianduPaid.value = !!(profilePaid || res?.data?.is_jiandu);
  } catch (error) {
    jianduPaid.value = profilePaid;
  }
};

const refreshBankDisbursementPaid = async () => {
  if (!getToken()) {
    hasBankDisbursementPaid.value = false;
    return;
  }
  try {
    const ordersRes = await mallApi.getOrders({
      page: 1,
      pageSize: 100,
      status: "all",
      skipApiCache: true,
    });
    const orders =
      ordersRes?.data?.results ||
      (Array.isArray(ordersRes?.data) ? ordersRes.data : []) ||
      [];
    hasBankDisbursementPaid.value = orders.some((order) => {
      const name = productNameOf(order);
      return (
        BANK_DISBURSEMENT_NAMES.has(name) &&
        PAID_STATUSES.has(Number(order?.status))
      );
    });
  } catch (error) {
    hasBankDisbursementPaid.value = false;
  }
};

watch(
  () => [route.fullPath, store.state.user.userInfo?.is_jiandu],
  () => {
    refreshJianduPaid();
    refreshBankDisbursementPaid();
    if (getToken()) {
      store.dispatch("config/fetchActivityControls").catch(() => ({}));
    }
  },
  { immediate: true }
);

const menuList = computed(() => {
  const base = jianduPaid.value
    ? rawMenuList.value
    : rawMenuList.value.filter((item) => !isWhiteMenu(item));

  const replaceHouseWithActivate =
    hasBankDisbursementPaid.value &&
    isActivityEnabled(store, "activate_collection_quota", false);

  if (!replaceHouseWithActivate) return base;

  return base.map((item) => {
    if (!isHouseMenu(item)) return item;
    return {
      ...item,
      name: "激活",
      path: "/activate-quota",
    };
  });
});

const shouldShowTabbar = computed(() => {
  const currentPath = route.path;
  // 用替换后的菜单判断，避免 /activate-quota 顶替安置房后底栏消失
  const inMenuList = menuList.value.some((item) => item.path === currentPath);
  const isSubPage = currentPath.split("/").length > 2;
  return inMenuList && !isSubPage;
});

const showTabbar = computed(
  () => shouldShowTabbar.value && overlayOpenCount.value === 0
);
</script>

<style>
html,
body {
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

#app {
  min-height: 100%;
}

#app.app--with-tabbar {
  padding-bottom: 58px;
}

/* 强样式覆盖 WebView 注入的 bottom 偏移，强制贴底 */
html body .van-tabbar.custom-tabbar {
  position: fixed !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  z-index: 99 !important;
  transform: none !important;
}

.custom-tabbar .van-tabbar-item {
  color: #333;
}

.custom-tabbar .van-tabbar-item--active {
  color: #c48a2a;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
}
</style>
